import React from "react";

// Same "find the matching upload in the general documents pool" pattern as
// the resume lookup in StaffCareerTimeline.js:71-79
// (`documents.find(doc => doc.doc_type === 'resume')`). Licenses can be
// plural, so the match also needs the title: the license modal uploads via
// `handleDocumentUpload("license", licenseData.type, licenseData.document)`
// (StaffProfileForm.js), so doc_type "license" + title === the license's
// own type is how a given row's file comes back after a reload.
const getLicenseDocument = (documents, licenseType) => {
  if (!Array.isArray(documents)) return null;
  return documents.find(
    (doc) => doc.doc_type === "license" && doc.title === licenseType
  );
};

const LicensesTrainingsSection = ({
  isEditing,
  formData,
  onAddLicense,
  onAddTraining,
  documents,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="bg-white p-6 border border-[#E6E6E6]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[11px] font-bold text-[#3C3C3C]">
            {formData.position === "goalkeeper_coach" ? "GK LICENSES" : "LICENSES & CERTIFICATIONS"}
          </h2>
          {isEditing && (
            <button
              onClick={onAddLicense}
              className="px-4 py-2 border border-[#E6E6E6] bg-white text-[#1E1E1E] rounded-lg"
            >
              + Add License
            </button>
          )}
        </div>

        <div className="space-y-4">
          {(formData.licenses || []).length === 0 && (
            <div className="p-4 text-sm text-gray-500 border border-dashed border-gray-300 rounded-md">
              No licenses added yet.
            </div>
          )}
          {(formData.licenses || []).map((license) => {
            const licenseDoc = getLicenseDocument(documents, license.type);
            return (
              <div key={license.id} className="border-b border-[#D6DBEA] p-4">
                <div className="flex items-center gap-5">
                  <div>
                    <div className="text-[#222A35] text-[20px] font-bold mb-2">{license.type}</div>
                    <div className="text-[#646B7E] text-[14px]">Issue Date: {license.issueDate} | License ID: {license.licenseId}</div>
                  </div>
                  {/* Opens the license's own uploaded file (see
                      getLicenseDocument above). Renders only when the
                      documents pool actually has a match — QAV5-405: this
                      button used to have no handler at all. */}
                  {licenseDoc && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => window.open(licenseDoc.url, "_blank")}
                        className="w-8 h-8 bg-[#F8F9FB] rounded flex items-center justify-center"
                        aria-label={`Open document for ${license.type}`}
                      >
                        <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.2929 7.36678L8.16621 13.4934C7.41565 14.244 6.39766 14.6657 5.33621 14.6657C4.27475 14.6657 3.25677 14.244 2.50621 13.4934C1.75565 12.7429 1.33398 11.7249 1.33398 10.6634C1.33398 9.60199 1.75565 8.584 2.50621 7.83344L8.63287 1.70678C9.13325 1.2064 9.8119 0.925293 10.5195 0.925293C11.2272 0.925293 11.9058 1.2064 12.4062 1.70678C12.9066 2.20715 13.1877 2.8858 13.1877 3.59344C13.1877 4.30108 12.9066 4.97973 12.4062 5.48011L6.27287 11.6068C6.02269 11.857 5.68336 11.9975 5.32954 11.9975C4.97572 11.9975 4.6364 11.857 4.38621 11.6068C4.13602 11.3566 3.99547 11.0173 3.99547 10.6634C3.99547 10.3096 4.13602 9.9703 4.38621 9.72011L10.0462 4.06678" stroke="#006BF3" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white p-6 border border-[#E6E6E6]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[11px] font-bold text-[#3C3C3C]">FURTHER TRAININGS / WORKSHOPS</h2>
          {isEditing && (
            <button
              onClick={onAddTraining}
              className="px-4 py-2 border border-[#E6E6E6] bg-white text-[#1E1E1E] rounded-lg"
            >
              + Add
            </button>
          )}
        </div>

        <div className="space-y-4">
          {(formData.trainings || []).length === 0 && (
            <div className="p-4 text-sm text-gray-500 border border-dashed border-gray-300 rounded-md">
              No trainings added yet.
            </div>
          )}
          {(formData.trainings || []).map((training) => (
            <div key={training.id} className="border-b border-[#D6DBEA] p-4">
              <div className="flex items-center gap-5">
                <div>
                  <div className="text-[#222A35] text-[20px] font-bold mb-2">{training.title}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="w-8 h-8 bg-[#F8F9FB] rounded flex items-center justify-center">
                    <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.2929 7.36678L8.16621 13.4934C7.41565 14.244 6.39766 14.6657 5.33621 14.6657C4.27475 14.6657 3.25677 14.244 2.50621 13.4934C1.75565 12.7429 1.33398 11.7249 1.33398 10.6634C1.33398 9.60199 1.75565 8.584 2.50621 7.83344L8.63287 1.70678C9.13325 1.2064 9.8119 0.925293 10.5195 0.925293C11.2272 0.925293 11.9058 1.2064 12.4062 1.70678C12.9066 2.20715 13.1877 2.8858 13.1877 3.59344C13.1877 4.30108 12.9066 4.97973 12.4062 5.48011L6.27287 11.6068C6.02269 11.857 5.68336 11.9975 5.32954 11.9975C4.97572 11.9975 4.6364 11.857 4.38621 11.6068C4.13602 11.3566 3.99547 11.0173 3.99547 10.6634C3.99547 10.3096 4.13602 9.9703 4.38621 9.72011L10.0462 4.06678" stroke="#006BF3" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LicensesTrainingsSection;

